import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { OutreachContact } from "schemas/dashboard";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

interface OutreachTemplatesInterface {
  templates: Template[];
  setTemplates: Dispatch<SetStateAction<Template[]>>;
  outreachTemplates: Template[];
  followupTemplates: Template[];
  fetchOutreachTemplatesLoading: boolean;
  setFetchOutreachTemplatesLoading: Dispatch<SetStateAction<boolean>>;
  saveTemplateLoading: boolean;
  setSaveTemplateLoading: Dispatch<SetStateAction<boolean>>;
  currentTemplateContentType: TemplateContentType;
  setCurrentTemplateContentType: Dispatch<SetStateAction<TemplateContentType>>;
  fetchOutreachTemplates: (
    ignoreLoading?: boolean,
  ) => Promise<[Template[] | undefined, Template[] | undefined]>;
  defaultTemplates: Template[];
  setDefaultTemplates: Dispatch<SetStateAction<Template[]>>;
  userHasNoTemplates: boolean;
  setUserHasNoTemplates: Dispatch<SetStateAction<boolean>>;
  templateWithIds: Template[];
  getLatestFollowUpEmail: (contact: OutreachContact) => Promise<string>;
  createTemplateDialogOpen: boolean;
  setCreateTemplateDialogOpen: Dispatch<SetStateAction<boolean>>;
  currentDraftBody: string;
  setCurrentDraftBody: Dispatch<SetStateAction<string>>;
  editTemplateDialogId: string | null;
  setEditTemplateDialogId: Dispatch<SetStateAction<string | null>>;
  associatedNicheId: number | null;
  setAssociatedNicheId: Dispatch<SetStateAction<number | null>>;
  askUserToEdit: boolean;
  setAskUserToEdit: Dispatch<SetStateAction<boolean>>;
  onSaveAsNew: ((template: Template) => void) | null;
  setOnSaveAsNew: Dispatch<
    SetStateAction<((template: Template) => void) | null>
  >;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  templates: [],
  setTemplates: defaultContextMissingFunction,
  outreachTemplates: [],
  followupTemplates: [],
  saveTemplateLoading: false,
  setSaveTemplateLoading: defaultContextMissingFunction,
  fetchOutreachTemplatesLoading: false,
  setFetchOutreachTemplatesLoading: defaultContextMissingFunction,
  currentTemplateContentType: TemplateContentType.OUTREACH,
  setCurrentTemplateContentType: defaultContextMissingFunction,
  fetchOutreachTemplates: defaultContextMissingFunction,
  defaultTemplates: [],
  setDefaultTemplates: defaultContextMissingFunction,
  userHasNoTemplates: false,
  setUserHasNoTemplates: defaultContextMissingFunction,
  templateWithIds: [],
  getLatestFollowUpEmail: defaultContextMissingFunction,
  createTemplateDialogOpen: false,
  setCreateTemplateDialogOpen: defaultContextMissingFunction,
  editTemplateDialogId: null,
  setEditTemplateDialogId: defaultContextMissingFunction,
  currentDraftBody: "",
  setCurrentDraftBody: defaultContextMissingFunction,
  associatedNicheId: null,
  setAssociatedNicheId: defaultContextMissingFunction,
  askUserToEdit: false,
  setAskUserToEdit: defaultContextMissingFunction,
  onSaveAsNew: null,
  setOnSaveAsNew: defaultContextMissingFunction,
};

const OutreachTemplatesContext =
  createContext<OutreachTemplatesInterface>(defaultInterface);

interface OutreachTemplatesProviderProps {
  children: React.ReactNode;
}

const DEFAULT_FOLLOWUP_BODY =
  "<p>Hi {First Name},</p> <p> I hope this message finds you well! 🌈 </p> <p> I wanted to follow up on my previous email to see if you had a chance to consider my proposal. I'm truly excited about the potential of collaborating with {Brand Name} and bringing our shared vision to life. </p> <p> Looking forward to hearing from you soon! </p>";

const OutreachTemplatesProvider = ({
  children,
}: OutreachTemplatesProviderProps) => {
  // Template
  const { currentOrg, emailSettings } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [defaultTemplates, setDefaultTemplates] = useState<Template[]>([]);
  const [userHasNoTemplates, setUserHasNoTemplates] = useState<boolean>(false);

  const [currentTemplateContentType, setCurrentTemplateContentType] =
    useState<TemplateContentType>(TemplateContentType.OUTREACH);
  const [fetchOutreachTemplatesLoading, setFetchOutreachTemplatesLoading] =
    useState<boolean>(true);
  const [saveTemplateLoading, setSaveTemplateLoading] =
    useState<boolean>(false);
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] =
    useState<boolean>(false);
  const [editTemplateDialogId, setEditTemplateDialogId] = useState<
    string | null
  >(null);
  const [associatedNicheId, setAssociatedNicheId] = useState<number | null>(
    null,
  );
  const [askUserToEdit, setAskUserToEdit] = useState(false);
  const [currentDraftBody, setCurrentDraftBody] = useState<string>("");
  const [onSaveAsNew, setOnSaveAsNew] = useState<
    ((template: Template) => void) | null
  >(null);

  const outreachTemplates = useMemo(() => {
    return templates?.filter?.(
      (x: Template) => x?.contentType === TemplateContentType.OUTREACH,
    );
  }, [templates]);

  const followupTemplates = useMemo(() => {
    return templates?.filter?.(
      (x: Template) => x?.contentType === TemplateContentType.FOLLOWUP,
    );
  }, [templates]);

  const templateWithIds = useMemo(() => {
    return templates?.filter?.((x: Template) => x?.id && !isNaN(x?.id));
  }, [templates]);

  const fetchOutreachTemplates = async (
    ignoreLoading: boolean = false,
  ): Promise<[Template[] | undefined, Template[] | undefined]> => {
    if (!currentOrg?.id) {
      return [undefined, undefined];
    }
    try {
      if (!ignoreLoading) {
        setFetchOutreachTemplatesLoading(true);
      }
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
      );
      let fetchedTemplates = res.templates;
      setTemplates(fetchedTemplates);
      let defaultTemplates = res.defaultTemplates || [];
      if (defaultTemplates.length === 0) {
        setUserHasNoTemplates(true);
      } else {
        setDefaultTemplates(defaultTemplates);
      }
      return [fetchedTemplates, defaultTemplates];
    } catch (error) {
      setErrorAlert(error);
    } finally {
      if (!ignoreLoading) {
        setFetchOutreachTemplatesLoading(false);
      }
    }
    return [undefined, undefined];
  };

  const getLatestFollowUpEmail = async (currentContact: OutreachContact) => {
    const hasTemplateSettings =
      emailSettings?.emailSettingTemplates &&
      emailSettings?.emailSettingTemplates?.length > 0;
    const presetTemplate = hasTemplateSettings
      ? emailSettings?.emailSettingTemplates?.find((x) => x.emailOrder === 1)
      : null;
    const presetId = presetTemplate?.templateId;

    const name = currentContact?.contactName;
    let firstName = name ? name.split(" ")?.[0] : "";
    let replacedName = firstName || `${currentContact?.brand?.name} Team`;

    let body: string | undefined = followupTemplates?.[0]?.body;
    if (presetId) {
      body = followupTemplates?.find((x) => x.id === presetId)?.body;
    }
    if (!body) body = DEFAULT_FOLLOWUP_BODY;
    body = body.replaceAll("{Brand Name}", currentContact?.brand?.name || "");
    body = body.replaceAll("{First Name}", replacedName);

    return body;
  };

  return (
    <OutreachTemplatesContext.Provider
      value={{
        templates,
        setTemplates,
        outreachTemplates,
        followupTemplates,
        fetchOutreachTemplatesLoading,
        setFetchOutreachTemplatesLoading,
        saveTemplateLoading,
        setSaveTemplateLoading,
        currentTemplateContentType,
        setCurrentTemplateContentType,
        fetchOutreachTemplates,
        defaultTemplates,
        setDefaultTemplates,
        userHasNoTemplates,
        setUserHasNoTemplates,
        templateWithIds,
        getLatestFollowUpEmail,
        createTemplateDialogOpen,
        setCreateTemplateDialogOpen,
        editTemplateDialogId,
        setEditTemplateDialogId,
        currentDraftBody,
        setCurrentDraftBody,
        associatedNicheId,
        setAssociatedNicheId,
        askUserToEdit,
        setAskUserToEdit,
        onSaveAsNew,
        setOnSaveAsNew,
      }}
    >
      {children}
    </OutreachTemplatesContext.Provider>
  );
};

export { OutreachTemplatesProvider, OutreachTemplatesContext };
